import { Customer, Office } from "@/swagger/models";
import router from "@/router/router";
import { customerService } from "@/services/customer";
import { officeService } from "@/services/office";
import Vue from "vue";
import Component from "vue-class-component";
import { eventbus } from "@/eventbus";

@Component({
    router,
})
export default class Context extends Vue {
    customers: Customer[] = [];
    customer: Customer | null = null;

    offices: Office[] = [];
    office: Office | null = null;

    async created() {
        eventbus.$on("auth:login", async () => {
            await this.loadCustomers();
            await this.preloadCustomer();

            // The following two are automatically called by preselecting a customer, so we don't
            // call them explicitly
            // await this.loadOffices();
            // await this.preloadOffice();
        });

        eventbus.$on("customer:create", async (customer: Customer) => {
            await this.loadCustomers();
            await this.selectCustomer(customer.id);
            this.$tosti.success({ message: `Switched to customer ${customer.name}` });
        });

        eventbus.$on("office:create", async (office: Office) => {
            await this.loadOffices();
            await this.selectOffice(office.id);
            this.$tosti.success({ message: `Switched to office ${office.name}` });
        });

        eventbus.$on("customer:update", async (customer: Customer) => {
            await this.loadCustomers();
            if (this.customer?.id === customer.id) this.customer = customer;
        });

        eventbus.$on("office:update", async (office: Office) => {
            await this.loadOffices();
            if (this.office?.id === office.id) this.office = office;
        });
    }

    // @Watch("$route.params", { deep: true, immediate: true })
    // async handleRouteChange(params: Route["params"]) {
    //     Don't do this. If the user starts editing an office, we should not necessarily switch to it
    //     Also, when the id of either one is 'new", this breaks things
    //     The preload handlers will automatically get the customer/office from the URL if other sources are
    //     unavailable.
    //     if (params.customerId) await this.selectCustomer(parseInt(params.customerId));
    //     if (params.officeId) await this.selectOffice(parseInt(params.officeId));
    // }

    async loadCustomers() {
        // console.log("Loading customers");

        this.customers = await customerService.find();

        // Try to re-select the customer to make sure it's still valid
        if (this.customer) await this.selectCustomer(this.customer.id);
    }

    async preloadCustomer() {
        // Try to load the customer (in order):
        // - from the url
        // - from localStorage
        // - the first available one

        if (this.$route.params.customerId) await this.selectCustomer(parseInt(this.$route.params.customerId));

        if (!this.customer && localStorage.getItem("ciao-last-customer"))
            await this.selectCustomer(parseInt(localStorage.getItem("ciao-last-customer")));

        if (!this.customer && this.customers.length) await this.selectCustomer(this.customers[0].id);
    }

    /**
     * Tries to set the current customer from the list of customers
     * @param id the customer to look for in this.customers
     * @returns boolean true if setting the customer was successful
     */
    async selectCustomer(id: number) {
        // console.log("Selecting customer");
        // Same customer? Do nothing
        if (id === this.customer?.id) return;

        // Try to find that customer. If it doesn't exist, we unset the current customer
        const customer = this.customers.find((c) => c.id === id);

        if (customer) {
            this.customer = customer;
            // console.log(`Setting customer to ${customer.name}`);
            localStorage.setItem("ciao-last-customer", customer.id.toString());

            await this.loadOffices();
            await this.preloadOffice();

            // await this.updateRoute();
        } else {
            this.customer = null;
        }
    }

    async loadOffices() {
        // console.log("Loading offices");
        // No customer? Unload offices and set current office to null
        if (!this.customer) {
            this.offices = [];
            this.office = null;
            return;
        }

        this.offices = await officeService.find({
            params: {
                customer: this.customer.id,
            },
        });

        // Try to re-select the office to make sure it's still valid
        if (this.office) await this.selectOffice(this.office.id);
    }

    async preloadOffice() {
        // console.log("Preloading office");
        // Try to load the customer (in order):
        // - from the url
        // - from localStorage
        // - the first available one

        this.office = null;

        if (this.$route.params.officeId) await this.selectOffice(parseInt(this.$route.params.officeId));

        if (!this.office && localStorage.getItem("ciao-last-office"))
            await this.selectOffice(parseInt(localStorage.getItem("ciao-last-office")));

        if (!this.office && this.offices.length) await this.selectOffice(this.offices[0].id);
    }

    async selectOffice(id: number): Promise<boolean> {
        // console.log("Selecting office", id);
        if (id === this.office?.id) return;

        const office = this.offices.find((c) => c.id === id);

        if (office) {
            this.office = office;
            // console.log(`Setting office to ${office.name}`);
            localStorage.setItem("ciao-last-office", office.id.toString());

            await this.updateRoute();
        } else this.office = null;
    }

    async updateRoute() {
        // console.log("Updating route");
        try {
            await this.$router.push({
                query: this.$route.query, // Keep query params
                params: {
                    customerId: this.customer?.id.toString(),
                    officeId: this.office?.id.toString(),
                },
            });
        } catch (e) {
            // Can't import the actual NavigationDuplicated class :(
            if (e.name === "NavigationDuplicated") {
                // Swallow the error
                // This can happen because a route can have params set that do not appear in the url
            } else {
                throw e;
            }
        }
    }
}

export const context = new Context();
Vue.prototype.$context = context;

export const useContext = () => ({
    context,
});

declare module "vue/types/vue" {
    interface Vue {
        $context: Context;
    }
}
